import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from '../../../../components/Intl/withIntl.js';
import { Card, CardBody, ButtonGroup, Button, Form, FormGroup, InputGroup, InputGroupText, Input, Label } from 'reactstrap';

import ReactGA4 from 'react-ga4';

import config from '../../../../config.js';
import storage from '../../../../util/storage.js';
import { defaultLanguage } from '../../../../../Intl/setup.js';

import { checkUserAccountRequest, loginRequest, checkEmail, getUserPreference, login, USER_AUTH_ATTEMPTS_ALLOWED } from '../../UserActions.js';
import { getLanguagePath } from '../../../Intl/IntlHelper.js';

import withRouter from '../../../../components/Router/WithRouter.js';
import Icon from '../../../../components/Content/Icon.js';
import UserForgotPassword from './UserForgotPassword.js';
import UserNewPassword from './UserNewPassword.js';

class UserLoginForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            step: props.step || (props.email ? 'password' : 'email'),
            displayForgotPasswordForm: !!props.code,
            isOnLogin: false,
            isOnCheckEmail: false,
            email: props.email || '',
            password: props.password || '',
            code: props.code || '',
            displayPassword: false,
            attempts: 0,
            errorFields: [],
            errorMessages: {},
        };
    }

    componentDidMount() {
        if(this.props.location && this.props.location.query && this.props.location.query.access_token) {
            this.props.dispatch(login(this.props.location.query.access_token));
        } else if(this.props.email && this.props.password) {
            this.handleLogin(new Event('autoLogin'));
        } else if(!this.state.email && storage && storage.getItem('userEmail')) {
            this.goToPasswordStep();
        }
    }

    handleSetEmailStep = () => this.setState({ step: 'email', errorFields: [], errorMessages: {} });

    handleSetPasswordStep = () => this.setState({ step: 'password' });

    handleGoToRegister = () => this.props.navigate(`/${this.props.intl.locale}/user/register`);

    handleChange = event => {
        const { target } = event;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        const { name } = target;
        this.setState(state => ({ [name]: value, ...this.clearFieldError(state, name) }));
    };

    handleBlurEmail = () => {
        if(this.state.email && !checkEmail(this.state.email)) {
            this.setFieldError('email', this.props.intl.formatMessage({ id: 'userEmailError', defaultMessage: 'Your email address is incorrect!' }));
        }
    };

    handleCheckEmail = event => {
        event && event.preventDefault();
        if(checkEmail(this.state.email)) {
            this.setState({ isOnCheckEmail: true });
            this.props.dispatch(checkUserAccountRequest(this.state.email.trim().toLowerCase())).then(result => {
                this.setState({ isOnCheckEmail: false });
                if(result && result.ok) {
                    this.setState({ step: result.needVerification ? 'verification' : 'password', errorFields: [], errorMessages: {} });
                    storage && storage.setItem('userEmail', this.state.email);
                } else {
                    this.setFieldError('email', this.props.intl.formatMessage({ id: 'userAccountExistError', defaultMessage: 'Your email address is not associated to an account!' }), true);
                }
            });
        } else {
            this.setFieldError('email', this.props.intl.formatMessage({ id: 'userEmailError', defaultMessage: 'Your email address is incorrect!' }), true);
        }
    };

    handleLogin = event => {
        event.preventDefault();
        if(this.state.email && this.state.password) {
            this.setState({ isOnLogin: true });
            this.props.dispatch(loginRequest(this.state.email.toLowerCase(), this.state.password)).then(user => {
                const newAttempsCount = this.state.attempts + (user ? 0 : 1);
                this.setState({
                    isOnLogin: false,
                    attempts: newAttempsCount,
                    displayForgotPasswordForm: newAttempsCount >= USER_AUTH_ATTEMPTS_ALLOWED,
                });
                if(!user) {
                    this.setFieldError('password', this.props.intl.formatMessage({
                        id: 'userLoginError',
                        defaultMessage: 'Wrong credentials! Please retry...',
                    }), true);
                } else if(!this.state.newPasswordRequired) {
					this.handleTrackAnalytics();
                    const redirection = this.props.appRedirect || this.props.redirect || '';
                    this.props.navigate((!redirection.includes('user/login') && redirection) || getLanguagePath('/', getUserPreference(user, 'language') || defaultLanguage));
                }
            });
        } else {
            const errorFields = [];
            const errorMessages = {};
            const requiredMessage = this.props.intl.formatMessage({ id: 'requiredField', defaultMessage: 'This field is required.' });
            if(!this.state.email) {
                errorFields.push('email');
                errorMessages.email = requiredMessage;
            }
            if(!this.state.password) {
                errorFields.push('password');
                errorMessages.password = requiredMessage;
            }
            this.setState({ errorFields, errorMessages }, this.focusFirstErrorField);
        }
    };

    handleForgotPassword = () => {
        this.setState({ displayForgotPasswordForm: true });
    };

    handleSubmit = event => {
        event && event.preventDefault();
        switch(this.state.step) {
            case 'email':
            this.handleCheckEmail(event);
            break;

            case 'password':
            default:
            this.handleLogin(event);
            break;
        }
    };

	handleTrackAnalytics = () => {
		if(typeof window !== 'undefined' && config.google.analytics.trackingCodeGA4) {
            // GA: New
            ReactGA4.event('login', {
				method: 'app',
			});
        }
        if(typeof window !== 'undefined' && window.fbq) {
            // TODO: Track login on Fb Pixel
        }
	};

    // focus is only requested after an explicit submit, never on blur validation (it would trap the user in the field)
    setFieldError(name, message, focus = false) {
        this.setState(state => ({
            errorFields: state.errorFields.includes(name) ? state.errorFields : [...state.errorFields, name],
            errorMessages: { ...state.errorMessages, [name]: message },
        }), () => focus && this.focusField(name));
    }

    goToPasswordStep() {
        this.setState({
            email: storage.getItem('userEmail'),
            step: 'password',
        });
    }

    hasErrorField(name) {
        return this.state.errorFields && this.state.errorFields.indexOf(name) !== -1;
    }

    // Returned as a partial state so it can be merged into an existing setState updater
    clearFieldError(state, name) {
        if(!state.errorFields.includes(name)) {
            return {};
        }
        const errorMessages = { ...state.errorMessages };
        delete errorMessages[name];
        return {
            errorFields: state.errorFields.filter(field => field !== name),
            errorMessages,
        };
    }

    focusFirstErrorField() {
        this.focusField(this.state.errorFields[0]);
    }

    // Moves keyboard and screen reader focus onto the field the user has to fix
    focusField(name) {
        if(typeof document === 'undefined' || !name) {
            return;
        }
        const field = document.getElementById(`${name}Field`);
        field && field.focus();
    }

    renderFieldError(name) {
        if(!this.hasErrorField(name)) {
            return null;
        }
        return (
            <div id={`${name}FieldError`} className="invalid-feedback d-block" role="alert">
                <Icon icon="danger" aria-hidden="true" /> {this.state.errorMessages[name]}
            </div>
        );
    }

    renderForm() {
        if(this.state.displayForgotPasswordForm) {
            return <UserForgotPassword email={this.state.email} code={this.state.code} closeForm={() => this.setState({ displayForgotPasswordForm: false })} redirect={this.props.redirect} />;
        }

        if(this.state.newPasswordRequired) {
            return <UserNewPassword user={this.state.user} userAttributes={this.state.userAttributes} redirect={this.props.redirect} />;
        }

        return (
            <Form onSubmit={this.handleSubmit} noValidate>
                {this.state.step === 'email' && (
                    <FormGroup className="mb-4">
                        <Label for="emailField"><FormattedMessage id="userEmail" defaultMessage="Email" /></Label>
                        <InputGroup>
                            <InputGroupText><Icon icon="mail" aria-hidden="true" /></InputGroupText>
                            <Input
                                type="email"
                                name="email"
                                id="emailField"
                                inputMode="email"
                                autoComplete="username"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck="false"
                                className={`form-control-user ${this.hasErrorField('email') ? 'is-invalid' : ''}`}
                                aria-invalid={this.hasErrorField('email')}
                                aria-describedby={this.hasErrorField('email') ? 'emailFieldError' : undefined}
                                onChange={this.handleChange}
                                onBlur={this.handleBlurEmail}
                                placeholder={this.props.intl.formatMessage({ id: 'userEmail', defaultMessage: 'Email' })}
                                value={this.state.email}
                                autoFocus
                            />
                        </InputGroup>
                        {this.renderFieldError('email')}
                    </FormGroup>
                )}

                {this.state.step === 'password' && (
                    <div>
                        {/* Keeps the username in the submitted form so password managers can match and save the credentials */}
                        <input type="text" name="username" autoComplete="username" value={this.state.email} readOnly tabIndex={-1} aria-hidden="true" className="visually-hidden" />
						<FormGroup>
							<Label for="emailField"><FormattedMessage id="userEmail" defaultMessage="Email" /></Label>
							<InputGroup>
								<InputGroupText>
									<Icon icon="mail" aria-hidden="true" />
								</InputGroupText>
								<Input value={this.state.email} disabled />
								<Button type="button" color="light" className="border" onClick={this.handleSetEmailStep}>
									<Icon icon="pen" fixedWidth />
								</Button>
							</InputGroup>
						</FormGroup>
                        <FormGroup>
                            <Label for="passwordField"><FormattedMessage id="userPassword" defaultMessage="Password" /></Label>
                            <InputGroup>
                                <InputGroupText><Icon icon="lock" aria-hidden="true" /></InputGroupText>
                                <Input
                                    type={this.state.displayPassword ? 'text' : 'password'}
                                    name="password"
                                    id="passwordField"
                                    autoComplete="current-password"
                                    className={this.hasErrorField('password') ? 'is-invalid' : ''}
                                    aria-invalid={this.hasErrorField('password')}
                                    aria-describedby={this.hasErrorField('password') ? 'passwordFieldError' : undefined}
                                    onChange={this.handleChange}
                                    placeholder={this.props.intl.formatMessage({ id: 'userPassword', defaultMessage: 'Password' })}
                                    value={this.state.password}
                                    autoFocus
                                />
                                <Button
                                    type="button"
                                    color="light"
                                    className="border"
                                    onClick={() => this.setState(state => ({ displayPassword: !state.displayPassword }))}
                                    aria-controls="passwordField"
                                    aria-pressed={this.state.displayPassword}
                                    title={this.props.intl.formatMessage(this.state.displayPassword ? { id: 'userPasswordHideButton', defaultMessage: 'Hide password' } : { id: 'userPasswordDisplayButton', defaultMessage: 'Display password' })}
                                    aria-label={this.props.intl.formatMessage(this.state.displayPassword ? { id: 'userPasswordHideButton', defaultMessage: 'Hide password' } : { id: 'userPasswordDisplayButton', defaultMessage: 'Display password' })}
                                >
                                    <Icon icon={this.state.displayPassword ? 'eye-off' : 'eye'} aria-hidden="true" />
                                </Button>
                            </InputGroup>
                            {this.renderFieldError('password')}
                        </FormGroup>
                        <div className="d-flex justify-content-end mb-3">
                            <Button type="button" color="link" size="sm" className="px-0 text-black-70" onClick={this.handleForgotPassword}>
                                <Icon icon="lock-unlock" aria-hidden="true" /> <FormattedMessage id="userForgotPassword" defaultMessage="Forgot password?" />
                            </Button>
                        </div>
                    </div>
                )}

                {this.state.step === 'verification' && (
                    <div className="text-center">
                        <p>
                            <Icon icon="info" /> <FormattedMessage id="userRecoverFromOldAppText" defaultMessage="You've been migrated to the new version! Check your mailbox to recover your password and activate your new account!" />
                        </p>
                        <ButtonGroup size="sm" className="my-2">
                            <Button type="button" color="light" onClick={this.handleSetEmailStep}>
                                <Icon icon="chevron-left" aria-hidden="true" /> <FormattedMessage id="back" defaultMessage="Back" />
                            </Button>
                            <Button type="button" color="primary" onClick={this.handleSetPasswordStep}>
                                <Icon icon="check" aria-hidden="true" /> <FormattedMessage id="userRecoverFromOldAppCheckButton" defaultMessage="I well received my recover password email" />
                            </Button>
                        </ButtonGroup>
                    </div>
                )}

                <FormGroup>
                    <div className="d-flex align-items-center gap-2">
                        {this.props.closeForm && (
                            <Button type="button" color="light" size="lg" onClick={event => this.props.closeForm()}>
                                <Icon icon="chevron-left" aria-hidden="true" /> <span className="d-none d-sm-inline"><FormattedMessage id="back" defaultMessage="Back" /></span>
                            </Button>
                        )}
                        {this.state.step === 'email' && (
                            <Button type="submit" color="danger" size="lg" className="flex-grow-1" disabled={!this.state.email || this.state.isOnCheckEmail} aria-busy={this.state.isOnCheckEmail}>
                                <FormattedMessage id="next" defaultMessage="Next" /> <Icon icon={this.state.isOnCheckEmail ? 'spinner' : 'chevron-right'} spin={this.state.isOnCheckEmail} aria-hidden="true" />
                            </Button>
                        )}
                        {this.state.step === 'password' && (
                            <Button type="submit" color="danger" size="lg" className="flex-grow-1" disabled={!this.state.email || !this.state.password || this.state.isOnLogin} aria-busy={this.state.isOnLogin}>
                                <FormattedMessage id="userLoginAction" defaultMessage="Login" /> <Icon icon={this.state.isOnLogin ? 'spinner' : 'log-off'} spin={this.state.isOnLogin} aria-hidden="true" />
                            </Button>
                        )}
                    </div>
                </FormGroup>

                {!this.props.hideRegisterButton && (
                    <div className="text-center border-top mt-5 pt-4">
                        <p className="text-black-70 mb-3">
                            <FormattedMessage id="userRegisterText" defaultMessage="Create your secure account, order in 1 click and save your orders easily!" />
                        </p>
                        <Button type="button" color="secondary" outline onClick={this.handleGoToRegister}>
                            <Icon icon="user-add" aria-hidden="true" /> <FormattedMessage id="userRegister" defaultMessage="Create my account" />
                        </Button>
                    </div>
                )}
            </Form>
        );
    }

    render() {
        if(this.props.noWrapper) {
            return this.renderForm();
        }

        return (
            <Card className="shadow">
                <CardBody>
                    {this.renderForm()}
                </CardBody>
            </Card>
        );
    }
}

function mapStateToProps(store, props) {
    return {
        appRedirect: store.app.redirect,
    };
}

UserLoginForm.defaultProps = {
    appRedirect: null,
    redirect: null,
    step: '',
    email: '',
    password: '',
    code: '',
    hideRegisterButton: false,
    noWrapper: false,
    closeForm: null,
};

UserLoginForm.propTypes = {
    history: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
    appRedirect: PropTypes.string,
    redirect: PropTypes.string,
    step: PropTypes.string,
    email: PropTypes.string,
    password: PropTypes.string,
    code: PropTypes.string,
    hideRegisterButton: PropTypes.bool,
    noWrapper: PropTypes.bool,
    closeForm: PropTypes.func,
};

export default connect(mapStateToProps)(withRouter(injectIntl(UserLoginForm)));
